import { SectionKey } from "@reactive-resume/schema";
import { cn } from "@reactive-resume/utils";
import { useCallback, useEffect, useRef, useState } from "react";

import { useResumeStore } from "@/client/stores/resume";

type Props = {
  id: SectionKey;
  name: string;
};

export const EditableSectionName = ({ id, name }: Props) => {
  const setValue = useResumeStore((state) => state.setValue);
  const [editing, setEditing] = useState(false);
  const [value, setLocalValue] = useState(name);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => setLocalValue(name), [name]);

  useEffect(() => {
    if (editing) inputRef.current?.select();
  }, [editing]);

  const commit = useCallback(() => {
    setEditing(false);
    const trimmed = value.trim();
    if (trimmed && trimmed !== name) {
      setValue(`sections.${id}.name`, trimmed);
    } else {
      setLocalValue(name);
    }
  }, [value, name, id, setValue]);

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setLocalValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          if (e.key === "Escape") {
            setLocalValue(name);
            setEditing(false);
          }
        }}
        className={cn(
          "line-clamp-1 w-full min-w-0 border-b border-primary/40 bg-transparent text-sm font-semibold outline-none",
        )}
      />
    );
  }

  return (
    <h2
      className="line-clamp-1 cursor-text text-sm font-semibold"
      onDoubleClick={() => setEditing(true)}
      title="Double-click to rename"
    >
      {name}
    </h2>
  );
};
